import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { 
  ALL_LANGUAGES, 
  LANGUAGES_BY_REGION,
  changeLanguage, 
  getCurrentLanguage, 
  getLanguageInfo,
  isFullyTranslated 
} from '../i18n';

const REGION_NAMES = {
  spain: 'España (Oficiales)',
  international: 'Internacional',
  europe: 'Europa',
  americas: 'Américas',
  asia: 'Asia',
  mena: 'Medio Oriente',
  africa: 'África'
};

const LanguageSelector = ({ variant = 'default', showFlag = true, showName = true, compact = false }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  
  const currentLang = getCurrentLanguage();
  const currentLangInfo = getLanguageInfo(currentLang) || ALL_LANGUAGES[0];
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
    setSearchQuery('');
  };
  
  // Filter languages by search
  const filteredLanguages = searchQuery 
    ? ALL_LANGUAGES.filter(lang => 
        lang.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lang.native.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lang.code.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;
  
  const getButtonClasses = () => {
    const base = 'flex items-center gap-2 transition-colors';
    
    switch (variant) {
      case 'minimal':
        return `${base} p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800`;
      case 'outline':
        return `${base} px-3 py-2 rounded-lg border border-slate-300 hover:border-indigo-500 hover:bg-slate-50`;
      case 'ghost':
        return `${base} px-3 py-2 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-900`;
      case 'nav':
        return `${base} px-2 py-1.5 rounded-md text-sm text-slate-600 hover:text-indigo-600 hover:bg-indigo-50`;
      default:
        return `${base} px-3 py-2 rounded-lg bg-white border border-slate-200 shadow-sm hover:border-indigo-300`;
    }
  };
  
  const renderLanguageItem = (lang, showBeta = true) => (
    <button
      key={lang.code}
      onClick={() => handleLanguageChange(lang.code)}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors ${
        currentLang === lang.code ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700'
      }`}
      data-testid={`language-option-${lang.code}`}
    >
      <span className="text-lg">{lang.flag}</span>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium block truncate">{lang.native}</span>
        {!compact && <span className="text-xs text-slate-400">{lang.name}</span>}
      </div>
      {showBeta && !isFullyTranslated(lang.code) && (
        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
          Beta
        </span>
      )}
      {currentLang === lang.code && (
        <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />
      )}
    </button>
  );
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={getButtonClasses()}
        aria-label={t('language.select', 'Seleccionar idioma')}
        data-testid="language-selector-btn"
      >
        {showFlag && <span className="text-lg">{currentLangInfo.flag}</span>}
        {variant === 'minimal' && !showName ? (
          <Globe className="w-5 h-5 text-slate-600" />
        ) : showName ? (
          <span className="text-sm font-medium">{currentLangInfo.native}</span>
        ) : null}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden"
          data-testid="language-dropdown"
        >
          {/* Search */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar idioma..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8 h-9 text-sm"
                autoFocus
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                >
                  <X className="w-3 h-3 text-slate-400" />
                </button>
              )}
            </div>
          </div>
          
          {/* Languages list */}
          <div className="max-h-80 overflow-y-auto">
            {filteredLanguages ? (
              // Search results
              <div>
                <div className="px-3 py-2 text-xs font-medium text-slate-500 bg-slate-50">
                  {filteredLanguages.length} resultados
                </div>
                {filteredLanguages.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-slate-500">
                    No se encontraron idiomas
                  </div>
                ) : (
                  filteredLanguages.map(lang => renderLanguageItem(lang))
                )}
              </div>
            ) : (
              // Grouped by region
              Object.entries(LANGUAGES_BY_REGION).map(([region, langs]) => {
                if (langs.length === 0) return null;
                return (
                  <div key={region}>
                    <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">
                      {REGION_NAMES[region] || region}
                    </div>
                    {langs.map(lang => renderLanguageItem(lang))}
                  </div>
                );
              })
            )}
          </div>
          
          {/* Footer */}
          <div className="border-t border-slate-100 px-3 py-2 bg-slate-50">
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {ALL_LANGUAGES.length} idiomas disponibles
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
